import { FC, useState } from "react";
import { Button, DatePicker, Empty, Input, Space, Table, Tag, Tooltip, Typography } from "antd";
import styled from "@emotion/styled";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { ParcelCustomerParcelItem, ParcelProductSnapshot } from "services/interface/parcelCustomers.interface";
import { useParcelCustomerParcels } from "hooks/useParcelCustomers";
import { formatDateWithTime } from "utils/dateFormat";

interface AgentParcelTabProps {
  agent_id: string;
}

const statusLabel: Record<string, string> = {
  PENDING: "รอดำเนินการ",
  IN_QUEUE: "กำลังประมวลผล",
  SUCCESS: "สำเร็จ",
  FAILED: "ล้มเหลว",
  CANCELLED: "ยกเลิก",
};

const statusColor: Record<string, string> = {
  PENDING: "default",
  IN_QUEUE: "processing",
  SUCCESS: "success",
  FAILED: "error",
  CANCELLED: "warning",
};

const PAGE_SIZE = 10;

const AgentParcelTab: FC<AgentParcelTabProps> = ({ agent_id }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedDate, setAppliedDate] = useState("");

  const offset = (currentPage - 1) * PAGE_SIZE;

  const { data, isLoading } = useParcelCustomerParcels(
    { agent_id, offset, limit: PAGE_SIZE, search: appliedSearch || undefined, date: appliedDate || undefined },
    !!agent_id,
  );

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedSearch(searchInput);
    setAppliedDate(dateInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setDateInput("");
    setCurrentPage(1);
    setAppliedSearch("");
    setAppliedDate("");
  };

  const columns: ColumnsType<ParcelCustomerParcelItem> = [
    {
      title: "วันที่ทำรายการ",
      dataIndex: "create_date",
      key: "create_date",
      width: 150,
      render: (v: string) => formatDateWithTime(v),
    },
    {
      title: "เลขพัสดุ",
      dataIndex: "tracking_number",
      key: "tracking_number",
      width: 160,
      render: (v: string | null) =>
        v ? (
          <Typography.Text copyable style={{ fontSize: 13 }}>
            {v}
          </Typography.Text>
        ) : (
          "-"
        ),
    },
    {
      title: "พัสดุที่ส่ง",
      dataIndex: "products_snapshot",
      key: "products_snapshot",
      width: 250,
      render: (v: ParcelProductSnapshot[] | null) => {
        if (!v?.length) return "-";
        const text = v.map((p) => `${p.name}${p.qty > 1 ? ` x${p.qty}` : ""}`).join(", ");
        return (
          <Tooltip title={text}>
            <Typography.Text ellipsis style={{ fontSize: 13, maxWidth: 230, display: "block" }}>
              {text}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: "ผู้รับ",
      dataIndex: "destination_name",
      key: "destination_name",
      width: 130,
      render: (v: string | null) => v || "-",
    },
    {
      title: "เบอร์โทร",
      dataIndex: "destination_telephone",
      key: "destination_telephone",
      width: 110,
      render: (v: string | null) => v || "-",
    },
    {
      title: "ที่อยู่จัดส่ง",
      dataIndex: "destination_address_text",
      key: "destination_address_text",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (v: string | null) => v || "-",
    },
    {
      title: "ราคา",
      dataIndex: "estimate_price",
      key: "estimate_price",
      width: 90,
      align: "right",
      render: (v: number | null) => (v != null ? `฿${v.toLocaleString()}` : "-"),
    },
    {
      title: "สถานะ",
      dataIndex: "speed_queue_status",
      key: "speed_queue_status",
      width: 120,
      render: (v: string) => (
        <Tag color={statusColor[v] || "default"}>{statusLabel[v] || v}</Tag>
      ),
    },
  ];

  const items = data?.data?.items || [];
  const total = data?.data?.total || 0;

  return (
    <WrapperAll>
      <HeadWrapper>
        <SearchWrapper>
          <Space.Compact>
            <Input
              style={{ height: "32px" }}
              placeholder="ค้นหาเลขติดตามพัสดุ"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onPressEnter={handleSearch}
            />
            <DatePicker
              style={{ width: "100%" }}
              placeholder="วันที่ทำรายการ"
              format="DD/M/YYYY"
              placement="topRight"
              showToday
              inputReadOnly
              value={dateInput ? dayjs(dateInput) : null}
              onChange={(date) => setDateInput(date ? date.format("YYYY-MM-DD") : "")}
            />
            <Button type="default" onClick={handleSearch}>
              ค้นหา
            </Button>
            {(searchInput || dateInput) && (
              <Button type="default" onClick={handleClear}>
                X
              </Button>
            )}
          </Space.Compact>
        </SearchWrapper>
      </HeadWrapper>

      <CardWrapperTable>
        {!isLoading && items.length === 0 ? (
          <Empty
            description="ยังไม่มีรายการพัสดุ"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "40px 0" }}
          />
        ) : (
          <Table<ParcelCustomerParcelItem>
            columns={columns}
            dataSource={items}
            loading={isLoading}
            rowKey="id"
            size="small"
            scroll={{ x: 700 }}
            pagination={{
              current: currentPage,
              pageSize: PAGE_SIZE,
              total,
              size: "small",
              onChange: setCurrentPage,
            }}
          />
        )}
      </CardWrapperTable>
    </WrapperAll>
  );
};

export default AgentParcelTab;

const WrapperAll = styled.div`
  padding: 10px 0px 10px 0px;
  background-color: #efefef;
  min-height: calc(100vh - 200px);
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const CardWrapperTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #ffffff;
  padding: 5px;
`;
